/**
 * 释放当前弹窗（Host / 路由跳转 / 付费墙销毁共用）
 *
 * - 清队列 current（dismiss）
 * - openWidgetOnDismiss 时：user、route_change 均可落挂件（源 2）
 * - 非 user 路径补 pop_up_no_show 诊断
 */

import { isActivityShowable, useActivityStore } from '@/store/activity';
import { useActivityQueueStore } from '@/store/activity-queue';
import { useActivitySessionStore } from '@/store/activity-session';

import { trackPopupNoShow } from './track-popup';

import type { TDismissReason } from './types';

/** 允许按 openWidgetOnDismiss 落挂件的关闭原因 */
const WIDGET_ELIGIBLE_REASONS: ReadonlySet<TDismissReason> = new Set(['user', 'route_change']);

/**
 * 释放当前弹窗并按配置决定是否写入挂件 session。
 * @param reason 关闭原因
 * @param taskId 可选；传入时做幂等校验，须与 current.id 一致
 * @returns 是否实际释放了 current
 */
export function releaseCurrentPopup(reason: TDismissReason, taskId?: string): boolean {
    const queue = useActivityQueueStore.getState();
    const { current } = queue;
    if (!current) {
        return false;
    }
    if (taskId != null && taskId !== current.id) {
        return false;
    }

    const { id, activityId, openWidgetOnDismiss, type } = current;

    // 源 2：配置允许且关闭原因在白名单内 → 落挂件
    if (openWidgetOnDismiss && WIDGET_ELIGIBLE_REASONS.has(reason)) {
        const activity = useActivityStore.getState().getActivity(activityId);
        if (activity && isActivityShowable(activity)) {
            useActivitySessionStore.getState().upsertSession({
                activityId,
                widgetVisible: true,
                widgetSource: 'popup_dismiss',
                createdAt: Date.now(),
            });
        }
    }

    if (reason !== 'user') {
        trackPopupNoShow({
            activityId,
            reason,
            surface: 'popup',
            extra: { pop_up_type: type },
        });
    }

    queue.dismiss(reason, id);
    return true;
}
